import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { FileDown, FileSpreadsheet, FileText, Search, ChevronRight, ArrowUpFromLine, ArrowDownToLine, Scale } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";
import { toast } from "sonner";

const NetCell = ({ value }) => (
  <span className={`font-semibold ${value >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inrShort(value)}</span>
);

export default function ProjectPnL() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [wbs, setWbs] = useState("");
  const [wbsDraft, setWbsDraft] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(() => {
    const params = { ...(search.trim() ? { search: search.trim() } : {}), ...(wbs.trim() ? { wbs: wbs.trim() } : {}) };
    axios.get(`${API}/reports/project-pnl`, { params }).then((r) => { setData(r.data); setExpanded(new Set()); }).catch(() => {});
  }, [search, wbs]);

  useEffect(() => {
    load();
    window.addEventListener("sap-synced", load);
    return () => window.removeEventListener("sap-synced", load);
  }, [load]);

  const toggle = (code) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });

  const download = async (format) => {
    try {
      const res = await axios.get(`${API}/reports/project-pnl/export`, {
        params: { ...(search.trim() ? { search: search.trim() } : {}), ...(wbs.trim() ? { wbs: wbs.trim() } : {}), format }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `project-pnl.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 200);
    } catch {
      toast.error("Could not generate the export. Please try again.");
    }
  };

  if (!data) return <div className="animate-pulse text-slate-400 text-sm">Loading project P&L...</div>;

  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="project-pnl-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Project P&amp;L</h1>
          <p className="text-sm text-slate-500 mt-1">Net position per project — AP cost vs AR billing, grouped by Project (first 4 chars of WBS)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="project-pnl-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="project-pnl-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="project-pnl-export-csv"><FileDown className="h-4 w-4 mr-1.5" />CSV</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
      <div className="relative w-80">
        <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search project code or name"
          className="w-80 h-9 pl-8 pr-8 border-slate-300 text-sm bg-white"
          data-testid="project-pnl-search-input"
        />
        {search && (
          <button type="button" onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
            data-testid="project-pnl-search-clear">✕</button>
        )}
      </div>
      <div className="relative w-72">
        <Input
          value={wbsDraft}
          onChange={(e) => setWbsDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && setWbs(wbsDraft)}
          onBlur={() => setWbs(wbsDraft)}
          placeholder="WBS filter: 2506* or *FUR* (comma-separated)"
          title="Plain code = all WBS starting with it; 2506* = starts with; *FUR* = contains. Press Enter to apply."
          className="w-72 h-9 pr-8 border-slate-300 text-sm bg-white font-mono"
          data-testid="project-pnl-wbs-input"
        />
        {wbsDraft && (
          <button type="button" onClick={() => { setWbsDraft(""); setWbs(""); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
            data-testid="project-pnl-wbs-clear">✕</button>
        )}
      </div>
      {wbs && <span className="text-xs text-slate-500" data-testid="project-pnl-wbs-active">WBS filter <b className="font-mono">{wbs.toUpperCase()}</b> · Enter to apply</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200 shadow-sm fade-in-up">
          <CardContent className="p-5 flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-2">Total AP Cost</p>
              <p className="text-2xl font-black tracking-tighter text-slate-900" data-testid="project-pnl-total-ap">{inrShort(t.ap_cost)}</p>
              <p className="text-xs mt-1.5 text-slate-400">{t.projects} projects</p>
            </div>
            <div className="h-9 w-9 rounded-md bg-sky-50 flex items-center justify-center"><ArrowUpFromLine className="h-4.5 w-4.5 text-sky-700" style={{ height: 18, width: 18 }} /></div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm fade-in-up">
          <CardContent className="p-5 flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-2">Total AR Billing</p>
              <p className="text-2xl font-black tracking-tighter text-slate-900" data-testid="project-pnl-total-ar">{inrShort(t.ar_billing)}</p>
              <p className="text-xs mt-1.5 text-slate-400">Net open outstanding</p>
            </div>
            <div className="h-9 w-9 rounded-md bg-amber-50 flex items-center justify-center"><ArrowDownToLine className="h-4.5 w-4.5 text-amber-600" style={{ height: 18, width: 18 }} /></div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm fade-in-up">
          <CardContent className="p-5 flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-2">Net Position (AR − AP)</p>
              <p className="text-2xl font-black tracking-tighter" data-testid="project-pnl-total-net"><NetCell value={t.net} /></p>
              <p className="text-xs mt-1.5 text-slate-400">{t.net >= 0 ? "Net receivable" : "Net payable"}</p>
            </div>
            <div className="h-9 w-9 rounded-md bg-emerald-50 flex items-center justify-center"><Scale className="h-4.5 w-4.5 text-emerald-600" style={{ height: 18, width: 18 }} /></div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-slate-800">Net Position by Project</CardTitle>
          <p className="text-xs text-slate-400 mt-0.5">Click a project to see its WBS breakdown · net open amounts (consistent with AP/AR Ageing)</p>
        </CardHeader>
        <CardContent>
          <Table data-testid="project-pnl-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Project / WBS</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">AP Cost</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">AR Billing</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Net Position</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((p) => {
                const open = expanded.has(p.project_code);
                return (
                  <Fragment key={p.project_code}>
                    <TableRow
                      key={p.project_code}
                      className="border-b border-slate-200 hover:bg-slate-50/60 cursor-pointer"
                      onClick={() => p.wbs.length && toggle(p.project_code)}
                      data-testid={`project-pnl-row-${p.project_code}`}
                    >
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2">
                          {p.wbs.length > 0
                            ? <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
                            : <span className="w-3.5" />}
                          <span className="font-semibold text-slate-800">{p.project_code}</span>
                          {p.project_name && <span className="text-xs text-slate-400 truncate">· {p.project_name}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{inrShort(p.ap_cost)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-700">{inrShort(p.ar_billing)}</TableCell>
                      <TableCell className="text-right text-sm"><NetCell value={p.net} /></TableCell>
                    </TableRow>
                    {open && p.wbs.map((w) => (
                      <TableRow key={`${p.project_code}~${w.wbs_element}`} className="bg-slate-50/50 border-b border-slate-100" data-testid={`project-pnl-wbs-${w.wbs_element}`}>
                        <TableCell className="text-sm">
                          <span className="font-mono text-slate-600 pl-8">{w.wbs_element}</span>
                        </TableCell>
                        <TableCell className="text-right text-sm text-slate-600">{inr(w.ap_cost)}</TableCell>
                        <TableCell className="text-right text-sm text-slate-600">{inr(w.ar_billing)}</TableCell>
                        <TableCell className="text-right text-sm"><NetCell value={w.net} /></TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                );
              })}
              {data.rows.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-slate-400 py-8">No projects match your search</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
